"use client"

import React, { createContext, useContext, useEffect, useRef, useState } from "react"

import FooterText from "./FooterText"
import FormContainer from "./FormContainer"
import HeaderSection from "./HeaderSection"
import NavBar from "./NavBar"
import ProgressBar from "./ProgressBar"
import SubmitButton from "./SubmitButton"

const SPANISH_TEXT = {
  continueButton: "Continuar",
  backButton: "Regresar",
  verifyButton: "Verificar",
}

export const FormErrorContext = createContext<(() => void) | null>(null)

export const useFormErrorContext = () => {
  return useContext(FormErrorContext)
}

interface WebNativeStepWrapperProps {
  title: string
  subtitle: string
  progressPercentage: number
  children: React.ReactNode
  isLoading?: boolean
  formError?: string | null
  onSubmit: (e: React.FormEvent) => void
  submitDisabled?: boolean
  submitButtonText?: React.ReactNode
  showBackButton?: boolean
  onBack?: () => void
  footerText?: React.ReactNode
}

const StepWrapper: React.FC<WebNativeStepWrapperProps> = ({
  title,
  subtitle,
  progressPercentage,
  children,
  isLoading = false,
  formError,
  onSubmit,
  submitDisabled = false,
  submitButtonText,
  showBackButton = false,
  onBack,
  footerText,
}) => {
  const buttonText = submitButtonText === undefined ? SPANISH_TEXT.continueButton : submitButtonText
  const [internalFormError, setInternalFormError] = useState<string | null>(formError || null)

  useEffect(() => {
    setInternalFormError(formError || null)

    if (formError && scrollContainerRef.current) {
      scrollToTop()
    }
  }, [formError])

  useEffect(() => {
    const adjustHeight = () => {
      if (typeof window === "undefined" || !scrollContainerRef.current) return
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty("--vh", `${vh}px`)
      scrollContainerRef.current.style.height = `calc(100 * var(--vh, 1vh))`

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      const isAndroid = /Android/i.test(navigator.userAgent)

      if (isIOS) {
        document.body.style.paddingBottom = "env(safe-area-inset-bottom, 20px)"
      } else if (isAndroid) {
        const deviceHeight = Math.max(window.innerHeight, screen.height)
        document.documentElement.style.setProperty("--android-height", `${deviceHeight}px`)
        scrollContainerRef.current.style.maxHeight = `var(--android-height, 100vh)`
      }
    }

    adjustHeight()

    if (typeof window !== "undefined") {
      window.addEventListener("resize", adjustHeight)
      window.addEventListener("orientationchange", adjustHeight)
    }

    const handleKeyboardHide = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.overflow = "auto"
      }
    }

    const checkForKeyboard = () => {
      if (typeof window === "undefined") return
      const lastHeight = window.innerHeight

      const currentHeight = window.innerHeight
      if (currentHeight < lastHeight) {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.style.height = `${currentHeight}px`
        }
      } else {
        adjustHeight()
        handleKeyboardHide()
      }
    }

    const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)
    if (isAndroid && typeof window !== "undefined") {
      window.addEventListener("resize", checkForKeyboard)
    }

    const handleBlur = () => {
      setTimeout(() => {
        if (typeof document !== "undefined" && document.activeElement === document.body) {
          handleKeyboardHide()
        }
      }, 100)
    }

    if (typeof document !== "undefined") {
      document.addEventListener("blur", handleBlur, true)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", adjustHeight)
        window.removeEventListener("orientationchange", adjustHeight)
        if (isAndroid) {
          window.removeEventListener("resize", checkForKeyboard)
        }
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("blur", handleBlur, true)
      }
    }
  }, [])

  const clearFormError = () => {
    if (internalFormError) {
      setInternalFormError(null)
    }
  }
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflow = "auto"
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }

  return (
    <FormErrorContext.Provider value={clearFormError}>
      <div
        ref={scrollContainerRef}
        className="relative flex h-full flex-col overflow-y-auto overflow-x-hidden bg-white font-nunito"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 20px)",
          minHeight: "100%",
        }}
      >
        <div className="sticky top-0 z-40 bg-white shadow-sm">
          <NavBar showBackButton={showBackButton} onBack={onBack} />
          <ProgressBar percentage={progressPercentage} />
        </div>

        <div className="mx-auto w-11/12 flex-1 px-0 pb-10 pt-6">
          <div className="flex flex-col">
            <HeaderSection title={title} subtitle={subtitle} />
            <FormContainer onSubmit={onSubmit} formError={internalFormError}>
              {children}
            </FormContainer>
            {submitButtonText !== "" && (
              <div
                className="mb-6 flex-none"
                onClick={() => {
                  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)
                  if (isAndroid && typeof document !== "undefined") {
                    const activeElement = document.activeElement as HTMLElement
                    if (activeElement && activeElement.blur) {
                      activeElement.blur()
                    }
                  }
                }}
              >
                <SubmitButton
                  text={buttonText}
                  isLoading={isLoading}
                  disabled={submitDisabled}
                  onClick={() => {
                    if (internalFormError) {
                      scrollToTop()
                    }
                  }}
                />
              </div>
            )}

            {footerText && (
              <div className="mb-16 bg-white">
                <FooterText text={footerText} />
              </div>
            )}
          </div>
        </div>
      </div>
    </FormErrorContext.Provider>
  )
}

export default StepWrapper
